import Render from '@Views/components/popin/layout/popin-communication-rfe.html'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { user } from '@Store/modules'
import {IStripeSubscription, IOfficeSubstitutes, ISubstitute} from '@Store/types'
import { StripeSubscriptionType } from '@Enums/stripe-subscription-type'
import { UserRfeStatus } from '@Enums/index'

interface ICommunicationRfeOptions {
	choice: string
	refuseReason: string
	substituteIds: string[]
}

@Render
@Component({})
export class PopinCommunicationRfe extends Vue {
	@Prop({ required: true }) options!: ICommunicationRfeOptions
	@Prop({default: false}) submitted: boolean
	@Prop({default: false}) loading: boolean

	selectedOption = ''
	refuseText = ''

	currentStep = 1

	selectedSubstitutes: string[] = []

	readonly MAX_LENGTH = 200
	readonly RFE_PACK_ANNUAL_PRICE = 57.60
	readonly RFE_PACK_START_DATE = new Date('2026-09-01')

	get hasSubstitutesStep(): boolean {
		return this.selectedOption === '1' && this.officesWithSubstitute.length > 0
	}

	get steps(): { number: number, label: string }[] {
		return [
			{ number: 1, label: this.$i18n.t('vm.components.popin.communication-rfe.stepper.platform').toString() },
			{ number: 2, label: this.$i18n.t('vm.components.popin.communication-rfe.stepper.substitutes').toString() },
			{ number: 3, label: this.$i18n.t('vm.components.popin.communication-rfe.stepper.recap').toString() }
		]
	}

	stepStatus(step: number): string {
		if (step === this.currentStep) return 'is-current'
		if (step < this.currentStep) return 'is-done'
		return 'is-todo'
	}

	get isFirstStep(): boolean {
		return this.currentStep === 1
	}

	get isLastStep(): boolean {
		return this.currentStep === 3
	}

	get canChooseLater(): boolean {
		return new Date() < this.RFE_PACK_START_DATE
	}

	get showConfirm(): boolean {
		if (this.selectedOption === '0') return true
		return this.isLastStep
	}

	get canGoNext(): boolean {
		if (this.currentStep === 1) {
			if (this.selectedOption === '') return false
			if (this.selectedOption === '0') return this.refuseText.trim().length > 0 && !this.isRefuseReasonTooLong
			return true
		}
		return true
	}

	get canConfirm(): boolean {
		if (this.selectedOption === '1') return true
		if (this.selectedOption === '0') return this.refuseText.trim().length > 0 && !this.isRefuseReasonTooLong
		return false
	}

	next(): void {
		if (!this.canGoNext) return
		if (this.currentStep === 1) {
			this.currentStep = this.hasSubstitutesStep ? 2 : 3
		} else if (this.currentStep === 2) {
			this.currentStep = 3
		}
	}

	back(): void {
		if (this.currentStep === 3) {
			this.currentStep = this.hasSubstitutesStep ? 2 : 1
		} else if (this.currentStep === 2) {
			this.currentStep = 1
		} else {
			this.$emit('cancel')
		}
	}

	confirm(): void {
		if (!this.canConfirm) return
		this.$emit('confirm')
	}

	get backLabel(): string {
		return this.isFirstStep
			? this.$i18n.t('vm.root.home.communication-rfe.no-choice').toString()
			: this.$i18n.t('vm.components.popin.communication-rfe.nav.back').toString()
	}

	get nextLabel(): string {
		return this.$i18n.t('vm.components.popin.communication-rfe.nav.next').toString()
	}

	get confirmLabel(): string {
		const key = this.selectedOption === '0' ? 'confirm-refuse' : 'confirm-accept'
		return this.$i18n.t(`vm.components.popin.communication-rfe.recap.${key}`).toString()
	}

	get packsCountLabel(): string {
		return this.$i18n.tc('vm.components.popin.communication-rfe.recap.packs-count', this.packCount, { count: this.packCount }).toString()
	}

	get holderName(): string {
		const coord = user.currentUser && user.currentUser.coordinates
		if (!coord) return ''
		return `${coord.first_name || ''} ${coord.last_name || ''}`.trim()
	}

	// Cabinets ayant au moins un remplaçant sans compte principal
	get officesWithSubstitute(): IOfficeSubstitutes[] {
		const current = user.currentUser
		return (current && current.substitutes || []).filter(office => this.substitutesOf(office).length > 0)
	}

	// Un remplaçant dont le pack RFE est déjà actif.
	private isSubstituteActivated(substitute: ISubstitute): boolean {
		return !!substitute.rfe && Number(substitute.rfe.status) === UserRfeStatus.Accepted
	}

	// un même remplaçant présent dans plusieurs cabinets
	// n'est affiché que dans le premier cabinet où il est détecté.
	// On exclut ceux déjà activés
	private get uniqueSubstitutesByOffice(): { [officeId: string]: ISubstitute[] } {
		const seen = new Set<string>()
		const result: { [officeId: string]: ISubstitute[] } = {}
		const offices = (user.currentUser && user.currentUser.substitutes) || []
		offices.forEach(office => {
			result[office.id] = (office.substitutes || []).filter(sub => {
				if (this.isSubstituteActivated(sub)) return false
				if (seen.has(sub.id)) return false
				seen.add(sub.id)
				return true
			})
		})
		return result
	}

	substitutesOf(office: IOfficeSubstitutes): ISubstitute[] {
		return this.uniqueSubstitutesByOffice[office.id] || []
	}

	substituteNameWithEmail(substitute: ISubstitute): string {
		return `${substitute.first_name || ''} ${substitute.last_name || ''} - (${substitute.email})`.trim()
	}


	substituteDisplayName(substitute: ISubstitute): string {
		return `${substitute.first_name || ''} ${substitute.last_name || ''}`.trim() || substitute.email
	}

	isSubstituteProfileComplete(substitute: ISubstitute): boolean {
		return !!substitute.siren && !!substitute.tva && !!substitute.naf
	}

	isSubstituteProfileIncomplete(substitute: ISubstitute): boolean {
		return !this.isSubstituteProfileComplete(substitute)
	}

	officeSelectedCount(office: IOfficeSubstitutes): number {
		return this.substitutesOf(office).filter(sub => this.isSubstituteSelected(office.id, sub.id)).length
	}

	isOfficeAllSelected(office: IOfficeSubstitutes): boolean {
		const subs = this.substitutesOf(office)
		return subs.length > 0 && subs.every(sub => this.isSubstituteSelected(office.id, sub.id))
	}

	get isAllSelected(): boolean {
		const offices = this.officesWithSubstitute
		return offices.length > 0 && offices.every(office => this.isOfficeAllSelected(office))
	}

	toggleAllSubstitutes(): void {
		const selectAll = !this.isAllSelected
		this.officesWithSubstitute.forEach(office => {
			this.substitutesOf(office).forEach(sub => this.toggleSubstitute(office.id, sub.id, selectAll))
		})
	}

	get selectedSubstituteDetails(): { office: IOfficeSubstitutes, substitute: ISubstitute }[] {
		const result: { office: IOfficeSubstitutes, substitute: ISubstitute }[] = []
		this.officesWithSubstitute.forEach(office => {
			this.substitutesOf(office).forEach(sub => {
				if (this.isSubstituteSelected(office.id, sub.id)) result.push({ office, substitute: sub })
			})
		})
		return result
	}

	private substituteKey(ofiId: string, userId: string): string {
		return `${ofiId}::${userId}`
	}

	isSubstituteSelected(ofiId: string, userId: string): boolean {
		return this.selectedSubstitutes.indexOf(this.substituteKey(ofiId, userId)) !== -1
	}

	toggleSubstitute(ofiId: string, userId: string, checked: boolean): void {
		const key = this.substituteKey(ofiId, userId)
		if (checked) {
			if (this.selectedSubstitutes.indexOf(key) === -1) this.selectedSubstitutes.push(key)
		} else {
			this.selectedSubstitutes = this.selectedSubstitutes.filter(k => k !== key)
		}
	}

	get selectedSubstituteIds(): string[] {
		const ids = this.selectedSubstitutes.map(key => key.split('::')[1])
		return Array.from(new Set(ids))
	}

	@Watch('selectedSubstitutes')
	onSelectedSubstitutesChange(): void {
		this.options.substituteIds = this.selectedSubstituteIds
	}

	get choices () {
		return [
			{
				value: '1',
				label: ''
			},
			{
				value: '0',
				label: this.$i18n.t('vm.components.popin.communication-rfe.choice.refuse').toString()
			},
		]
	}

	private get subscription(): IStripeSubscription {
		return user.currentUser && user.currentUser.stripeSubscription
	}

	get isAnnualSubscription(): boolean {
		return !!this.subscription && Number(this.subscription.value) === StripeSubscriptionType.year
	}

	private get prorataStartDate(): Date {
		const now = new Date()
		return now < this.RFE_PACK_START_DATE ? this.RFE_PACK_START_DATE : now
	}

	get rfePackProrataAmount(): number {
		if (!this.subscription || !this.subscription.session_date) return 0

		const endDate = new Date(this.subscription.session_date)
		const msPerDay = 1000 * 60 * 60 * 24
		const remainingDays = Math.max(0, Math.ceil((endDate.getTime() - this.prorataStartDate.getTime()) / msPerDay))

		return (this.RFE_PACK_ANNUAL_PRICE / 365) * remainingDays
	}

	get selectedSubstituteCount(): number {
		return this.selectedSubstitutes.length
	}

	get substituteUnitPrice(): number {
		return this.isAnnualSubscription ? this.rfePackProrataAmount : this.RFE_PACK_ANNUAL_PRICE / 12
	}

	get packCount(): number {
		return 1 + this.selectedSubstituteCount
	}

	get packUnitRecurring(): number {
		return this.isAnnualSubscription ? this.RFE_PACK_ANNUAL_PRICE : this.RFE_PACK_ANNUAL_PRICE / 12
	}

	get nextInvoiceTotal(): number {
		return this.substituteUnitPrice * this.packCount
	}

	get recurringTotal(): number {
		return this.packUnitRecurring * this.packCount
	}

	get subscriptionEndDate(): Date {
		return this.subscription && this.subscription.session_date ? new Date(this.subscription.session_date) : null
	}

	get prorataStartDateShort(): string {
		return this.formatDateShort(this.prorataStartDate)
	}

	get subscriptionEndDateShort(): string {
		return this.subscriptionEndDate ? this.formatDateShort(this.subscriptionEndDate) : ''
	}

	// Sous-titre de l'étape « Remplaçants »
	// Annuel : {0} montant prorata, {1} début de période (01/09/2026 avant cette
	// date, sinon date du jour), {2} date de renouvellement, {3} montant récurrent.
	get substituteStepSubtitle(): string {
		const key = this.isAnnualSubscription ? 'subtitle-annual' : 'subtitle-monthly'
		return this.$i18n.t(`vm.components.popin.communication-rfe.substitutes.${key}`, [
			this.formatAmount(this.substituteUnitPrice),
			this.formatDateShort(this.prorataStartDate),
			this.subscriptionEndDateShort,
			this.formatAmount(this.packUnitRecurring)
		]).toString()
	}

	formatAmount(amount: number): string {
		return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € TTC`
	}

	formatDateShort(date: Date): string {
		return new Intl.DateTimeFormat('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		}).format(date)
	}

	get isRefuseReasonTooLong(): boolean {
		return this.refuseText.length > this.MAX_LENGTH
	}

	get message(): string {
		return this.isRefuseReasonTooLong
			? this.$i18n.t('vm.components.popin.communication-rfe.refuse-reason.maxLength-warning', [this.MAX_LENGTH]).toString()
			: ''
	}

	@Watch('selectedOption')
	onOptionChange(): void {
		this.options.choice = this.selectedOption

		if (this.selectedOption !== '1') this.selectedSubstitutes = []
	}

	@Watch('refuseText')
	onRefuseTextChange(): void {
		this.options.refuseReason = this.refuseText.slice(0, this.MAX_LENGTH)
	}

}
